// frontend/src/utils/draftBlanks.ts

/**
 * MORNING2 - the blank rule: what only Joseph knows stays a MARKED blank.
 *
 * The AI drafts an email reply for his approval. Where it needs a fact only he has (a routing
 * number, an amount, a date he has not decided), it leaves a bracketed blank such as
 * "[routing number]" instead of guessing. The card paints those blanks yellow, and Send stays off
 * while one is left in the text.
 *
 * MIRRORED BY HAND from backend/src/services/draftBlanks.ts - the frontend never imports backend
 * source (green locally, red on Vercel). Change the rule there and change it here, and the same
 * cases in both .test.ts files. splitBlanks exists only on this side: only the card needs it.
 */

// One blank = a "[...]" token with 1-60 characters inside, and no "[", "]" or line break inside.
// - Capped at 60 so a stray pair of brackets around a whole sentence is not read as a blank.
// - No line break, so a bracket opened in one paragraph never pairs with one in the next.
// - No bracket inside, so nested brackets give the innermost pair: "[a [b] c]" finds "[b]".
const BLANK_PATTERN = /\[[^[\]\r\n]{1,60}\]/g;

/**
 * Every blank in `text`, brackets kept, de-duplicated, in the order each was first seen.
 * Run on the AI's draft once, and the result is what gets STORED with the draft.
 */
export function blanksIn(text: string): string[] {
  const seen = new Set<string>();
  const found: string[] = [];
  // A fresh match per call: BLANK_PATTERN is global, so matchAll walks its own copy and the shared
  // lastIndex is never touched.
  for (const match of text.matchAll(BLANK_PATTERN)) {
    if (!seen.has(match[0])) {
      seen.add(match[0]);
      found.push(match[0]);
    }
  }
  return found;
}

/**
 * Which of the STORED blanks are still in `body`. This is what keeps Send off.
 *
 * It checks only the stored blanks, verbatim (an exact substring), never "any square bracket":
 * his own "[see attached]" must never block a send. A blank he filled in, or whose brackets he
 * deleted, is no longer an exact match and so no longer counts.
 * Returned in stored order, each once.
 */
export function blanksLeft(body: string, blanks: readonly string[]): string[] {
  const left: string[] = [];
  for (const blank of blanks) {
    // An empty stored blank would match every body and block Send for good, so it counts for nothing.
    if (blank.length === 0) continue;
    if (left.includes(blank)) continue;
    if (body.includes(blank)) left.push(blank);
  }
  return left;
}

export interface BodySegment {
  text: string;
  /** True when this segment is one of the stored blanks: the card paints it yellow. */
  blank: boolean;
}

const escapeForRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The body as ordered segments, so the card can paint each stored blank without touching the text.
 *
 * Every occurrence of a stored blank is its own `blank: true` segment; the text between them is
 * `blank: false`. An empty piece is never emitted (so an empty body is no segments at all, and a
 * body with no blank is one segment). Joining the segments' text gives back the body exactly.
 *
 * Only the STORED blanks are marked, the same rule as blanksLeft: his own "[see attached]" stays plain.
 * Stored blanks never contain a bracket by construction, so they cannot overlap; if a hand-edited
 * list ever did (say "[a]" and "[a] [b]"), the longest one wins at a given spot, and the scan is
 * left to right, so nothing is marked twice and no text is lost.
 */
export function splitBlanks(body: string, blanks: readonly string[]): BodySegment[] {
  if (body.length === 0) return [];
  // Longest first: a regex alternation takes the first alternative that matches at a position, so
  // sorting by length makes the longest stored blank win where two start at the same place.
  const usable = [...new Set(blanks.filter((blank) => blank.length > 0))].sort((a, b) => b.length - a.length);
  if (usable.length === 0) return [{ text: body, blank: false }];

  const pattern = new RegExp(usable.map(escapeForRegExp).join('|'), 'g');
  const segments: BodySegment[] = [];
  let cursor = 0;
  for (const match of body.matchAll(pattern)) {
    const start = match.index ?? 0;
    if (start > cursor) segments.push({ text: body.slice(cursor, start), blank: false });
    segments.push({ text: match[0], blank: true });
    cursor = start + match[0].length;
  }
  if (cursor < body.length) segments.push({ text: body.slice(cursor), blank: false });
  return segments;
}
